import React, { useEffect, useState } from "react";
import { deleteListing, markListingSold, reactivateListing } from "../lib/listings";
import { subscribeToMyListingRating, subscribeToSellerRatingAggregate, submitSellerRating } from "../lib/sellerRatings";
import { T } from "../theme";
import { Badge, Btn, Card, fmt, SectionTitle, StarRating, TapeIcon } from "../ui";

const TYPE_LABEL = { sale: "For Sale", trade: "For Trade", both: "For Sale or Trade" };

export default function ListingDetailScreen({ listing, isMine, viewerUserId, onBack }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const [sellerRating, setSellerRating] = useState({ ratingSum: 0, ratingCount: 0, ratingAverage: 0 });
  const [myRating, setMyRating] = useState(0);

  useEffect(() => {
    const unsubAggregate = subscribeToSellerRatingAggregate(listing.sellerUserId, setSellerRating);
    const unsubMine = viewerUserId && !isMine
      ? subscribeToMyListingRating(listing.id, viewerUserId, setMyRating)
      : () => {};
    return () => { unsubAggregate(); unsubMine(); };
  }, [listing.sellerUserId, listing.id, viewerUserId, isMine]);

  const rateSeller = (n) => submitSellerRating(listing.id, listing.sellerUserId, viewerUserId, n);

  const remove = async () => {
    setBusy(true);
    try {
      await deleteListing(listing.id);
      onBack();
    } finally {
      setBusy(false);
    }
  };

  const toggleSold = async () => {
    setBusy(true);
    try {
      if (listing.status === "sold") await reactivateListing(listing.id);
      else await markListingSold(listing.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Card style={{ background: `linear-gradient(135deg,${T.surface},${T.card})`, textAlign: "center", padding: "24px 16px" }}>
        {listing.mainPhotoUrl ? (
          <img src={listing.mainPhotoUrl} alt={listing.albumTitle} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 10, border: `1px solid ${T.border}` }} />
        ) : (
          <TapeIcon size={80} />
        )}
        <div style={{ fontSize: 22, fontWeight: "bold", color: T.text, marginTop: 12 }}>{listing.albumTitle}</div>
        <div style={{ fontSize: 14, color: T.muted, marginTop: 4 }}>{listing.artist}</div>
        <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{listing.releaseYear || ""}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
          Sold by <span style={{ color: T.text, fontWeight: "bold" }}>{listing.sellerDisplayName || "Collector"}</span>
          {sellerRating.ratingCount > 0 && (
            <span style={{ color: T.accent }}> · {sellerRating.ratingAverage.toFixed(1)} ★ ({sellerRating.ratingCount})</span>
          )}
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          {listing.condition && <Badge condition={listing.condition} />}
          <span style={{ fontSize: 9, background: T.statBg, border: `1px solid ${T.border}`, color: T.accent, fontWeight: "bold", padding: "2px 7px", borderRadius: 4 }}>{TYPE_LABEL[listing.listingType]}</span>
          {listing.status === "sold" && <span style={{ fontSize: 9, background: T.border, color: T.muted, padding: "2px 7px", borderRadius: 4 }}>SOLD</span>}
        </div>
      </Card>

      {listing.price != null && (
        <Card style={{ textAlign: "center" }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase" }}>Asking Price</div>
          <div style={{ fontSize: 24, fontWeight: "bold", color: T.value, marginTop: 4 }}>{fmt(listing.price)}</div>
        </Card>
      )}

      {listing.tradeNotes && (
        <>
          <SectionTitle>Looking to Trade For</SectionTitle>
          <Card><div style={{ fontSize: 13, color: T.text }}>{listing.tradeNotes}</div></Card>
        </>
      )}

      <SectionTitle>Description &amp; Contact</SectionTitle>
      <Card><div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>{listing.description}</div></Card>

      {!isMine && (
        <div style={{ fontSize: 10, color: T.muted, textAlign: "center", marginTop: 4 }}>
          Payments and shipping are arranged directly between collectors — 8Track Vault doesn't process transactions yet.
        </div>
      )}

      {!isMine && listing.status === "sold" && (
        <>
          <SectionTitle>Rate This Seller</SectionTitle>
          <Card>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>
              {myRating > 0 ? "Your Rating" : "Completed a trade with this seller? Rate them"}
            </div>
            <StarRating value={myRating} onChange={rateSeller} />
          </Card>
        </>
      )}

      {isMine && confirming ? (
        <Card style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>Remove this listing? This cannot be undone.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="outline" onClick={() => setConfirming(false)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn variant="danger" disabled={busy} onClick={remove} style={{ flex: 1 }}>{busy ? "Removing…" : "Remove"}</Btn>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Btn variant="outline" onClick={onBack} style={{ flex: 1 }}>← Back</Btn>
          {isMine && (
            <>
              <Btn disabled={busy} onClick={toggleSold} style={{ flex: 1 }}>{listing.status === "sold" ? "Reactivate" : "Mark Sold"}</Btn>
              <Btn variant="danger" onClick={() => setConfirming(true)} style={{ flex: 1 }}>Remove</Btn>
            </>
          )}
        </div>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
